/**
 * @file file.component.tsx
 * @description Компонент для отображения и работы с файлом в списке вложений с контекстным меню
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-03
 * @updated 2025-09-10
 * @dependencies ['@consta/icons/IconKebab', '@src/components/icons/download-file-round/download-file-round.icon', '@consta/uikit/ContextMenu', '@components/icons/attach-file', '@utils/file-helper', '@consta/uikit/Text', '@src/components/icons/edit-file-round/edit-file-round.icon', '@components/icons/close-file-round/close-file-roind.icon', '@forms/modals/rename-file', 'react', '@src/types']
 * @related-files ['src/components/ui/data/attachment-list/attachment-list.component.tsx']
 */

import { IconKebab } from '@consta/icons/IconKebab';
import { DownloadFileRoundIcon } from '@src/components/icons/download-file-round/download-file-round.icon';
import { ContextMenu } from '@consta/uikit/ContextMenu';
import { AttachFileIcon } from '@components/icons/attach-file';
import { downloadFile, showFile } from '@utils/file-helper';
import { Text } from '@consta/uikit/Text';
import { EditFileRoundIcon } from '@src/components/icons/edit-file-round/edit-file-round.icon';
import { CloseFileRoundIcon } from '@components/icons/close-file-round/close-file-roind.icon';
import { RenameFile as RenameFileModal } from '@forms/modals/rename-file';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IAttachmentComponentProps } from '../attachment-list.component';
import { TAttachment } from '@src/types';

export const File = ({ onChange, attachment }: IAttachmentComponentProps) => {
    const [renamingFile, setRenamingFile] = useState<TAttachment | null>(null);
    const [isShowContext, setIsShowContext] = useState<boolean>(false);
    const anchorRef = useRef<HTMLElement | null>(null);
    const currentFile = useRef<TAttachment | null>(null);

    useEffect(() => {
        if (!setIsShowContext) {
            currentFile.current = null;
        }
    }, [setIsShowContext]);

    const contextMenuItems = useMemo(
        () => [
            {
                label: 'Скачать',
                icon: <DownloadFileRoundIcon className="hidden-buttons__button" />,
                onClick: () => {
                    currentFile.current?.link && downloadFile(currentFile.current.link, currentFile.current.name);
                }
            },
            {
                label: 'Редактировать',
                icon: <EditFileRoundIcon className="hidden-buttons__button" />,
                onClick: () => setRenamingFile(currentFile.current)
            },
            {
                label: 'Удалить',
                icon: <CloseFileRoundIcon className="hidden-buttons__button" />,
                onClick: () => {
                    setIsShowContext(false);
                    currentFile.current && onChange(currentFile.current, 'delete');
                }
            }
        ],
        []
    );

    const onKebabButtonClick = (e: React.MouseEvent<HTMLElement>) => {
        anchorRef.current = e.target as HTMLElement;
        setIsShowContext(true);
    };

    return (
        <>
            <AttachFileIcon
                onClick={() => showFile(attachment.link)}
                className="cursor-pointer attachment-list__file_icon"
            />

            <Text
                view="primary"
                size="m"
                className="attachment-list__file_name break-all text-[#045A9A] cursor-pointer hover:text-link-hover"
                onClick={() => showFile(attachment.link)}
            >
                {attachment.name}
            </Text>

            <div className="attachment-list__file_button h-full cursor-pointer">
                <IconKebab
                    size="xs"
                    view="brand"
                    onClick={(e) => {
                        onKebabButtonClick(e);
                        currentFile.current = attachment;
                    }}
                />
            </div>

            <ContextMenu
                items={contextMenuItems}
                isOpen={isShowContext}
                anchorRef={anchorRef}
                getItemLeftSide={(item) => item.icon}
                onClickOutside={() => setIsShowContext(false)}
                direction="downStartRight"
                style={{ pointerEvents: 'auto' }}
                className="absolute"
            />

            {Boolean(renamingFile) && (
                <RenameFileModal
                    setIsShow={() => setRenamingFile(null)}
                    title="Переименовать файл"
                    file={renamingFile}
                    onFileRename={(newFileName: string) =>
                        onChange({ ...attachment, name: newFileName }, 'edit', attachment)
                    }
                />
            )}
        </>
    );
};
